/**
 * Open the property's preferred tour scheduler without navigating away.
 *
 * Priority:
 *   1. PERQ tools (Fatwin hub) when installed — click the Request / Schedule
 *      a Tour CTA in the hub popup (in-page only).
 *   2. EliseAI widget — `window.eliseAi.onOpenSST()`.
 *   3. Only when neither provider successfully opens, navigate to
 *      `/schedule-a-tour`.
 *
 * Important: cancel the CTA href only after an in-page scheduler actually
 * opens. Stub/`eliseAi` objects without `onOpenSST`, or a PERQ host with no
 * dial DOM, used to claim the click and leave the button dead.
 */

export const SCHEDULE_TOUR_PATH = '/schedule-a-tour';

const PERQ_HUB_IDS = [
  'perq-online-guided-shopping-hub',
  'perq-online-guided-shopping',
] as const;

type EliseAiApi = {
  onOpenSST?: () => void;
};

type FwHubParams = {
  requestTourOn?: boolean;
  scheduleTourOn?: boolean;
};

type FwGlobal = {
  hubClientParameters?: FwHubParams;
};

type ScheduleTourClickEvent = {
  preventDefault: () => void;
  stopPropagation?: () => void;
  nativeEvent?: { stopImmediatePropagation?: () => void };
};

function getWindow(): Window | null {
  return typeof window === 'undefined' ? null : window;
}

function getElise(): EliseAiApi | null {
  return (getWindow() as (Window & { eliseAi?: EliseAiApi }) | null)?.eliseAi ?? null;
}

/** True when the CTA URL is the in-theme schedule-tour page (or empty). */
export function isScheduleTourPath(url: string | null | undefined): boolean {
  if (!url) return true;
  const trimmed = url.trim();
  if (!trimmed || trimmed === '#' || trimmed === SCHEDULE_TOUR_PATH) return true;
  try {
    const base = getWindow()?.location?.origin || 'https://example.com';
    const parsed = new URL(trimmed, base);
    return parsed.pathname.replace(/\/+$/, '') === SCHEDULE_TOUR_PATH;
  } catch {
    return trimmed.replace(/\/+$/, '') === SCHEDULE_TOUR_PATH;
  }
}

/**
 * Href to render on schedule-tour CTAs. Keeps the real in-theme path so
 * middle-click / open-in-new-tab still work; the click handler cancels
 * left-click navigation only when an in-page scheduler opens.
 * Custom editor URLs are left alone.
 */
export function scheduleTourHref(url: string | null | undefined): string {
  const href = (url || '').trim() || SCHEDULE_TOUR_PATH;
  return isScheduleTourPath(href) ? SCHEDULE_TOUR_PATH : href;
}

function resolveScheduleTourDestination(url: string | null | undefined): string {
  const href = (url || '').trim() || SCHEDULE_TOUR_PATH;
  if (!isScheduleTourPath(href)) return href;
  if (href.startsWith('#')) return SCHEDULE_TOUR_PATH;
  try {
    const base = getWindow()?.location?.origin || 'https://example.com';
    const parsed = new URL(href, base);
    if (parsed.origin === base || href.startsWith('/')) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}` || SCHEDULE_TOUR_PATH;
    }
    return parsed.toString();
  } catch {
    return SCHEDULE_TOUR_PATH;
  }
}

function getPerqHubParams(): FwHubParams | null {
  const w = getWindow() as (Window & { _FW?: FwGlobal }) | null;
  return w?._FW?.hubClientParameters ?? null;
}

function getPerqRoots(): Array<ParentNode> {
  const roots: ParentNode[] = [];
  for (const id of PERQ_HUB_IDS) {
    const host = document.getElementById(id);
    if (!host) continue;
    if (host.shadowRoot) roots.push(host.shadowRoot);
    roots.push(host);
  }
  return roots;
}

/** PERQ tools are considered installed when the hub script painted a host or exposed hub params. */
export function hasPerqTools(): boolean {
  if (!getWindow()) return false;
  const params = getPerqHubParams();
  if (params?.requestTourOn || params?.scheduleTourOn) return true;
  return PERQ_HUB_IDS.some((id) => Boolean(document.getElementById(id)));
}

/** True when any in-page tour scheduler is injected on this page. */
export function hasInPageTourScheduler(): boolean {
  return hasPerqTools() || Boolean(getElise()?.onOpenSST);
}

function clickFirst(root: ParentNode, selectors: string[]): boolean {
  for (const sel of selectors) {
    const el = root.querySelector<HTMLElement>(sel);
    if (el) {
      el.click();
      return true;
    }
  }
  return false;
}

function clickByLabel(root: ParentNode): boolean {
  const nodes = root.querySelectorAll<HTMLElement>('button, a, [role="button"]');
  for (const el of nodes) {
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (/^(schedule|request)\s+a\s+tour$/i.test(text)) {
      el.click();
      return true;
    }
  }
  return false;
}

/**
 * Prefer schedule-tour when that tool is on; otherwise request-tour (often
 * labeled "Schedule a Tour" in PERQ config).
 */
function perqCtaSelectors(): string[] {
  const params = getPerqHubParams();
  const scheduleFirst = Boolean(params?.scheduleTourOn);
  const schedule = [
    '.schedule-tour-cta',
    'a.schedule-tour-cta',
    'button.schedule-tour-cta',
    '[class*="schedule-tour-cta"]',
  ];
  const request = [
    '.request-tour-cta',
    'a.request-tour-cta',
    'button.request-tour-cta',
    '[class*="request-tour-cta"]',
  ];
  return scheduleFirst ? [...schedule, ...request] : [...request, ...schedule];
}

function tryClickPerqCta(): boolean {
  const selectors = perqCtaSelectors();
  for (const root of getPerqRoots()) {
    if (clickFirst(root, selectors)) return true;
    if (clickByLabel(root)) return true;
  }
  return false;
}

function tryOpenPerqHubButton(): boolean {
  for (const root of getPerqRoots()) {
    if (clickFirst(root, ['#hub-button', 'button#hub-button', '[id="hub-button"]'])) {
      return true;
    }
  }
  return false;
}

/**
 * Open PERQ on the current page only (CTA click or hub dial). Never reloads
 * with `?perqexp=` — that left the page feeling like a navigation.
 * @returns true only when a PERQ control was actually clicked.
 */
function tryOpenPerqScheduler(): boolean {
  if (!hasPerqTools()) return false;
  if (tryClickPerqCta()) return true;

  // Expand the speed dial, then retry the tour CTA on the next frame.
  if (tryOpenPerqHubButton()) {
    getWindow()?.requestAnimationFrame(() => {
      tryClickPerqCta();
    });
    return true;
  }

  // Hub marker is on the page but nothing is interactive yet — let the
  // caller fall through to /schedule-a-tour instead of eating the click.
  return false;
}

/**
 * @returns true only when Elise's schedule-tour API was invoked.
 */
function tryOpenEliseScheduler(): boolean {
  const elise = getElise();
  if (!elise || typeof elise.onOpenSST !== 'function') return false;
  elise.onOpenSST();
  return true;
}

function stopScheduleTourNavigation(event: ScheduleTourClickEvent): void {
  event.preventDefault();
  event.stopPropagation?.();
  event.nativeEvent?.stopImmediatePropagation?.();
}

/**
 * Open PERQ (if installed) or Elise on the current page.
 * @returns true when an in-page scheduler claimed the interaction.
 */
export function openScheduleTour(_fallbackUrl?: string): boolean {
  if (!getWindow()) return false;
  if (tryOpenPerqScheduler()) return true;
  if (tryOpenEliseScheduler()) return true;
  return false;
}

/**
 * Click handler for schedule-tour anchors.
 *
 * Tries PERQ / Elise first. Cancels default navigation only when one of them
 * successfully opens; otherwise navigates to `/schedule-a-tour` (or the
 * resolved in-theme URL).
 */
export function handleScheduleTourClick(
  event: ScheduleTourClickEvent,
  url: string | null | undefined,
): void {
  const href = (url || '').trim() || SCHEDULE_TOUR_PATH;
  if (!isScheduleTourPath(href)) return;

  if (tryOpenPerqScheduler()) {
    stopScheduleTourNavigation(event);
    return;
  }

  if (tryOpenEliseScheduler()) {
    stopScheduleTourNavigation(event);
    return;
  }

  // No working in-page scheduler — go to the form page. Always assign so a
  // leftover `href="#"` (or a prevented SPA race) cannot leave the CTA inert.
  stopScheduleTourNavigation(event);
  getWindow()?.location.assign(resolveScheduleTourDestination(href));
}
